import React from 'react';
import Button from '@mui/material/Button';
import makeStyles from '@mui/styles/makeStyles';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import UpdateIcon from '@mui/icons-material/Update';
import CancelIcon from '@mui/icons-material/Cancel';
import PublishIcon from '@mui/icons-material/Publish';
import DoneIcon from '@mui/icons-material/Done';
import RemoveIcon from '@mui/icons-material/Remove';
import ReconcileIcon from '@mui/icons-material/CallMerge';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import GetAppIcon from '@mui/icons-material/GetApp';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import DynamicFeedIcon from '@mui/icons-material/DynamicFeed';
import CompareArrowsTwoTone from '@mui/icons-material/CompareArrowsTwoTone';
import { green } from '@mui/material/colors';
import CircularProgress from '@mui/material/CircularProgress';

const useStyles = makeStyles(theme => ({
  leftIcon: {
    marginRight: theme.spacing(1),
  },
  rightIcon: {
    marginLeft: theme.spacing(1),
  },
  iconSmall: {
    fontSize: 20,
  },
  progressButtonWrapper: {
    position: 'relative',
  },
  buttonProgress: {
    color: green[400],
    position: 'absolute',
    top: '25%',
    left: '45%',
    marginTop: -6,
    marginLeft: -6,
  },
}));

function BaseButton({
  disabled = false,
  buttonText = '',
  children,
  iconClass = null,
  onClickPromise = null,
  onClick,
  color,
  className = '',
  ...otherProps
}) {
  const classes = useStyles();
  const theme = useTheme();

  const content = buttonText || children;
  const [isLoading, setIsLoading] = React.useState(false);

  const customStyle = {};
  if (color === 'primary') {
    customStyle.color = theme.palette.secondary.main;
  } else if (color === 'secondary') {
    customStyle.color = theme.palette.primary.main;
  }

  const IconClass = iconClass;

  return (
    <div className={`${classes.progressButtonWrapper} ${className}`}>
      <Button
        variant="contained"
        color={color}
        className={classes.button}
        onClick={(e) => {
          if (isLoading) return;
          onClick && onClick(e);
          if (onClickPromise) {
            const promise = onClickPromise();
            if (promise) {
              setIsLoading(true);
              promise.finally(() => setIsLoading(false));
            }
          }
        }}
        disabled={disabled}
        {...otherProps}
      >
        {iconClass && <IconClass className={classes.leftIcon} />}
        {content}
      </Button>
      {isLoading && (
        <CircularProgress
          thickness={6}
          size={30}
          className={classes.buttonProgress}
          style={customStyle}
        />
      )}
    </div>
  );
}

BaseButton.propTypes = {
  disabled: PropTypes.bool,
  buttonText: PropTypes.string,
  iconClass: PropTypes.object,
  onClickPromise: PropTypes.func,
  ...Button.propTypes,
};

const createIconButton = (iconClass, color, buttonText) => props => (
  <BaseButton
    color={color}
    iconClass={iconClass}
    buttonText={buttonText}
    {...props}
  />
);
export const AddButton = createIconButton(AddIcon, 'primary', 'add');
export const SaveButton = createIconButton(SaveIcon, 'primary', 'save');
export const UpdateButton = createIconButton(UpdateIcon, 'primary', 'update');
export const CloseButton = createIconButton(CancelIcon, 'secondary', 'close');
export const UploadButton = createIconButton(PublishIcon, 'primary', 'upload');
export const ApproveButton = createIconButton(DoneIcon, 'primary', 'convert');
export const RemoveButton = createIconButton(RemoveIcon, 'secondary', 'remove');
export const ReconcileButton = createIconButton(
  ReconcileIcon,
  'primary',
  'remove',
);
export const BackButton = createIconButton(ArrowBackIcon, 'primary', 'remove');
export const ProcessButton = createIconButton(SendIcon, 'primary', 'process');
export const ExportButton = createIconButton(PublishIcon, 'primary', 'export');
export const ImportButton = createIconButton(GetAppIcon, 'primary', 'import');
export const TransferButton = createIconButton(
  CompareArrowsTwoTone,
  'primary',
  'transfer',
);
export const EditButton = createIconButton(EditIcon, 'primary', 'edit');
export const DownloadButton = createIconButton(
  GetAppIcon,
  'primary',
  'download',
);
export const InfoButton = createIconButton(InfoIcon, 'primary', 'info');
export const BatchButton = createIconButton(
  DynamicFeedIcon,
  'primary',
  'batch',
);

export default BaseButton;
